import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "../landing/modern/Navbar";
import ModernFooter from "../landing/modern/ModernFooter";
import SEOHead from "./SEOHead";

interface ComparisonPoint {
  feature: string;
  youwoai: string | boolean;
  competitor: string | boolean;
}

interface SEOLandingPageProps {
  // SEO
  title: string;
  metaDescription: string;
  keywords: string;
  canonicalUrl: string;

  // Content
  headline: string;
  subheadline: string;
  heroIcon: string;
  heroIconColor: string;
  heroIconBg: string;

  // Problem/Solution
  problemTitle: string;
  problemDescription: string;
  solutionTitle: string;
  solutionDescription: string;

  // Comparison (optional)
  competitorName?: string;
  comparisonPoints?: ComparisonPoint[];

  // Features to highlight
  features: {
    icon: string;
    title: string;
    description: string;
  }[];

  // CTA
  ctaText: string;
  ctaLink: string;

  // Related tools
  relatedTools: {
    name: string;
    href: string;
    icon: string;
  }[];
}

/**
 * SEO-focused landing page for targeting specific search keywords.
 * These pages are designed to rank for long-tail keywords and convert visitors.
 */
const SEOLandingPage: React.FC<SEOLandingPageProps> = ({
  title,
  metaDescription,
  keywords,
  canonicalUrl,
  headline,
  subheadline,
  heroIcon,
  heroIconColor,
  heroIconBg,
  problemTitle,
  problemDescription,
  solutionTitle,
  solutionDescription,
  competitorName,
  comparisonPoints,
  features,
  ctaText,
  ctaLink,
  relatedTools,
}) => {
  const { t } = useTranslation();
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": metaDescription,
    "url": canonicalUrl,
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={title}
        description={metaDescription}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${heroIconBg}`}>
                <Icon icon={heroIcon} className={`text-4xl ${heroIconColor}`} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {headline}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                as="a"
                href={ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                className="bg-cornflowerblue text-white font-semibold px-8 py-6 no-underline"
              >
                {ctaText}
                <Icon icon="lucide:arrow-right" className="ml-2" />
              </Button>
              <Button
                as={Link}
                to="/tools"
                variant="bordered"
                size="lg"
                className="border-gray-300 text-gray-700 font-medium px-8 py-6 no-underline"
              >
                {t("seo.exploreAllTools")}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Problem */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-red-50 rounded-2xl p-8"
            >
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <Icon icon="lucide:x" className="text-red-500 text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{problemTitle}</h2>
              <p className="text-gray-600 leading-relaxed">{problemDescription}</p>
            </motion.div>

            {/* Solution */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-green-50 rounded-2xl p-8"
            >
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Icon icon="lucide:check" className="text-green-500 text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{solutionTitle}</h2>
              <p className="text-gray-600 leading-relaxed">{solutionDescription}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table (if competitor provided) */}
      {competitorName && comparisonPoints && (
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                YouWo AI vs {competitorName}
              </h2>
              <p className="text-gray-600">{t("seo.seeWhyStudentsChoose")}</p>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-3 bg-gray-100 p-4 font-semibold text-gray-700">
                  <div>{t("seo.feature")}</div>
                  <div className="text-center text-cornflowerblue">YouWo AI</div>
                  <div className="text-center">{competitorName}</div>
                </div>

                {/* Table Rows */}
                {comparisonPoints.map((point, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-3 p-4 border-t border-gray-100 items-center"
                  >
                    <div className="text-gray-700">{point.feature}</div>
                    <div className="text-center">
                      {typeof point.youwoai === "boolean" ? (
                        point.youwoai ? (
                          <Icon icon="lucide:check" className="text-green-500 text-xl mx-auto" />
                        ) : (
                          <Icon icon="lucide:x" className="text-red-400 text-xl mx-auto" />
                        )
                      ) : (
                        <span className="text-cornflowerblue font-medium">{point.youwoai}</span>
                      )}
                    </div>
                    <div className="text-center">
                      {typeof point.competitor === "boolean" ? (
                        point.competitor ? (
                          <Icon icon="lucide:check" className="text-green-500 text-xl mx-auto" />
                        ) : (
                          <Icon icon="lucide:x" className="text-red-400 text-xl mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-600">{point.competitor}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t("seo.whatMakesDifferent")}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                  <Icon icon={feature.icon} className="text-cornflowerblue text-2xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-cornflowerblue to-blue-600">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t("seo.readyToTransform")}
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              {t("seo.joinThousands")}
            </p>
            <Button
              as="a"
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              className="bg-white text-cornflowerblue font-semibold px-8 py-6 no-underline"
            >
              {t("seo.getStartedFree")}
              <Icon icon="lucide:arrow-right" className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Related Tools */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t("seo.exploreMoreTools")}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {relatedTools.map((tool, index) => (
              <Link
                key={index}
                to={tool.href}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 hover:border-cornflowerblue hover:shadow-md transition-all no-underline text-gray-700 hover:text-cornflowerblue"
              >
                <Icon icon={tool.icon} className="text-lg" />
                <span className="text-sm font-medium">{tool.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
};

export default SEOLandingPage;
